import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Title, useCanBlock, useDataProvider } from 'react-admin'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Box, Skeleton, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { __t } from '../../../i18n/t'
import FeatureIntro from '../../../components/FeatureIntro'
import { getChainId, getUserId } from '../../../lib'
import getChainName from '../../../lib/getChainName'
import getEmployeeType from '../../../lib/getEmployeeType'
import { EmployeeType } from '../../../models'
import ManualLinkButton from '../../../manual/components/ManualLinkButton'
import { useAudienceCount } from './data/useAudienceCount'
import { useDiscountCtx } from './data/useDiscountCtx'
import {
  Audience, Ctx, DiscountKind, Draft, RecipeId, StepKey, WizardView, ambiguousFailInfo, applyRecipe, checkFailInfo, duplicateDraft,
  errorsFor, failInfo, firstInvalidStep, freshDraft, isLocked, pickType, receipts, replacements, savedNote, savedStatus, savedTitle, selIds,
  sentenceRich, stepInfo, stepKeys, toDefault, turnOnNotify,
} from './model'
import { describeServerErrors, findSavedDuplicate, findSplitCodes, fromRouterState, isAmbiguousFailure, toPayload } from './payload'
import { SavedInfo, SavedScreen } from './SavedScreen'
import { StepNotice } from './steps/StepNotice'
import { StepReview } from './steps/StepReview'
import { StepType } from './steps/StepType'
import { StepValues } from './steps/StepValues'
import { AudienceSection } from './steps/who/AudienceSection'
import { WhenSection } from './steps/who/WhenSection'
import { ErrorSummary } from './shell/ErrorSummary'
import { Footer } from './shell/Footer'
import { LeaveDialog } from './shell/LeaveDialog'
import { LeaveGuard } from './shell/LeaveGuard'
import { Stepper } from './shell/Stepper'
import { useElementWidth } from './shell/useElementWidth'
import { WizardContext, Wiz } from './state/WizardContext'
import { Callout } from './ui/basics'
import { OutlineButton, TextButton } from './ui/controls'
import { focusTop } from './ui/focus'
import {
  C, buttonsRowSx, fillSx, kitCardSx, manualButtonSx, retryButtonSx, stackStartSx,
} from './ui/tokens'

// Link da lista aberto em outra aba (fora do JSX: o plugin de i18n embrulha literais de texto dentro de condicionais).
const LIST_LINK = { href: '#/discounts', target: '_blank', rel: 'noopener noreferrer' }
const NARROW_WIDTH = 600

interface Failure {
  title: string
  text: string
  button: string
  lines: string[]
  /** Resposta perdida: o desconto pode já existir; oferece abrir a lista numa aba nova para conferir. */
  checkList: boolean
}

/** Mensagem do servidor para o dono do posto: campos legíveis no 400; texto do Boom nos outros 4xx; nada técnico no 5xx. */
function describeFailure(error: any, payload: ReturnType<typeof toPayload>, c: Ctx, hasReplacements: boolean): Failure {
  const status = Number(error?.status)
  // Sem resposta (rede, 504): não dá para dizer que "nenhum desconto foi criado", porque ele pode ter sido.
  if (isAmbiguousFailure(error)) return { ...ambiguousFailInfo(), lines: [], checkList: true }
  const lines = describeServerErrors(error?.body?.errors, payload, c)
  if (!lines.length && status >= 400 && status < 500 && error?.message) lines.push(__t(String(error.message)))
  return { ...failInfo(hasReplacements), lines, checkList: false }
}

const NoChain: React.FC = () => (
  <Box sx={{ ...kitCardSx, p: 3, maxWidth: 560, mx: 'auto', mt: 3 }}>
    <Typography sx={{ fontSize: 16, color: C.ink }}>{__t('Selecione uma Rede')}</Typography>
    <Box sx={{ fontSize: 14, color: C.muted, mt: 0.5 }}>{__t('Escolha a rede na barra de cima para cadastrar o desconto nela.')}</Box>
  </Box>
)

const Loading: React.FC = () => (
  <Box sx={{ ...kitCardSx, p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }} aria-busy="true">
    <Skeleton variant="rounded" height={28} width="60%" />
    <Skeleton variant="rounded" height={18} width="80%" />
    <Skeleton variant="rounded" height={120} />
    <Skeleton variant="rounded" height={120} />
  </Box>
)

const Wizard: React.FC<{ chainId: string }> = ({ chainId }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dataProvider = useDataProvider()
  const queryClient = useQueryClient()
  const [boxRef, boxWidth] = useElementWidth<HTMLDivElement>()
  // o menu lateral come 280 px a partir de 600 px de janela: o que manda nos espaços é a largura do conteúdo, não a da janela
  const narrow = boxWidth > 0 && boxWidth < NARROW_WIDTH

  // "agora" atualiza a cada minuto: períodos, horários e "daqui a 10 minutos" dependem dele
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60 * 1000)
    return () => window.clearInterval(t)
  }, [])

  // entrada com dados preenchidos (clonar, relatório, Painel 360): lida uma vez
  const [prefill] = useState(() => fromRouterState(location.state, { now: new Date() }))
  const [draft, setDraft] = useState<Draft>(() => prefill?.draft ?? freshDraft())
  const initial = useRef(draft)
  const startStep = prefill && prefill.draft.kind && !isLocked(prefill.draft) ? 1 : 0
  const [stepIdx, setStepIdx] = useState(startStep)
  const [maxStep, setMaxStep] = useState(startStep)
  const [attempted, setAttempted] = useState<Partial<Record<StepKey, boolean>>>({})
  const [view, setView] = useState<WizardView>(WizardView.form)
  const [copyNote, setCopyNote] = useState(prefill?.note ?? '')
  const [failure, setFailure] = useState<Failure | null>(null)
  const [saved, setSaved] = useState<SavedInfo | null>(null)
  const [sameText, setSameText] = useState('')
  const [compactSteps, setCompactSteps] = useState(false)
  const savingRef = useRef(false)
  const lastSaved = useRef<{ draft: Draft; ctx: Ctx } | null>(null)
  const receiptCtx = useRef<Ctx | null>(null)
  const firstRender = useRef(true)
  // Resposta perdida (rede, 504): o servidor pode ter criado o desconto. Antes de criar de novo, confere.
  const attemptStart = useRef<number | null>(null)
  const unsure = useRef(false)

  const keys = stepKeys(draft.kind)
  const idx = Math.min(stepIdx, keys.length - 1)
  const stepKey = keys[idx]
  const last = idx === keys.length - 1
  const saving = view === WizardView.saving
  const showingSaved = view === WizardView.saved

  const special = draft.kind === DiscountKind.special
  const data = useDiscountCtx(chainId, now, { loadRecent: stepKey === StepKey.review, loadCities: special && draft.useCity })

  // Quantos clientes o público escolhido alcança: aparece na etapa do público e explica a notificação.
  const notifying = special && draft.notify
  const audienceActive = special && (draft.audience === Audience.all || draft.audience === Audience.some)
  const restricted = draft.audience === Audience.some || draft.useCity
  const audienceFilter = useMemo(() => (audienceActive && restricted
    ? {
      tagIds: draft.audience === Audience.some ? draft.tags : [],
      groupIds: draft.audience === Audience.some && draft.useGroups ? draft.groups : [],
      cityIds: draft.useCity ? draft.cities : [],
    }
    : null), [audienceActive, restricted, draft.audience, draft.tags, draft.useGroups, draft.groups, draft.useCity, draft.cities])
  const audienceCount = useAudienceCount(chainId, audienceFilter, audienceActive && (stepKey === StepKey.who || notifying), data.cityStats)
  const counts = useMemo(() => ({
    chain: audienceFilter === null ? audienceCount.count : null,
    audience: audienceCount.count,
    audienceExact: audienceCount.exact,
  }), [audienceFilter, audienceCount.count, audienceCount.exact])
  const ctx = useMemo<Ctx | null>(() => (data.ctx ? { ...data.ctx, counts } : null), [data.ctx, counts])

  // mexer em qualquer campo apaga o aviso de falha ao salvar (ele falava do rascunho de antes)
  const set = useCallback((fn: (d: Draft) => Draft) => {
    setDraft(fn)
    setFailure(f => (f ? null : f))
    receiptCtx.current = null
  }, [])
  const resetFlow = useCallback((d: Draft, step: number, note = '') => {
    setDraft(d)
    initial.current = d
    setStepIdx(step)
    setMaxStep(step)
    setAttempted({})
    setView(WizardView.form)
    setSaved(null)
    setFailure(null)
    setCopyNote(note)
    setSameText('')
    receiptCtx.current = null
    attemptStart.current = null
    unsure.current = false
    focusTop('step-q')
  }, [])

  // foco no título a cada troca de etapa (leitor de tela e teclado)
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return }
    focusTop('step-q')
  }, [idx])

  // Cadastro pela metade: fechar a aba, o Voltar do navegador (ou o gesto de voltar do Android), o menu e os links da
  // própria tela perguntam antes de sair (LeaveGuard). Sem roteador com dados, só o botão Cancelar pergunta.
  const dirty = (view === WizardView.form || saving) && draft !== initial.current
  const canBlock = useCanBlock()
  const [askLeave, setAskLeave] = useState(false)
  useEffect(() => {
    if (!dirty) return undefined
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const goStep = useCallback((i: number) => {
    if (saving || i < 0 || i > maxStep || i === idx) return
    setStepIdx(i)
  }, [saving, maxStep, idx])

  const act = useMemo(() => ({
    pickType: (kind: DiscountKind) => {
      if (saving) return
      setDraft(d => pickType(d, kind))
      setAttempted({})
      setMaxStep(m => Math.min(m, 1))
    },
    applyRecipe: (id: RecipeId) => {
      if (saving) return
      setDraft(d => applyRecipe(id, d, new Date()))
      setStepIdx(1)
      setMaxStep(1)
      setAttempted({})
    },
    toDefault: () => {
      if (saving) return
      setDraft(d => toDefault(d))
      setStepIdx(1)
      setMaxStep(1)
      setAttempted({})
    },
    turnOnNotify: () => { if (ctx) setDraft(d => turnOnNotify(d, ctx)) },
    turnOffNotify: () => { setDraft(d => ({ ...d, notify: false })) },
  }), [saving, ctx])

  const errors = ctx && attempted[stepKey] ? errorsFor(stepKey, draft, ctx) : []
  const fe = useMemo(() => {
    const out: Record<string, string> = {}
    errors.forEach(e => { if (!out[e.id]) out[e.id] = e.msg })
    return out
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.map(e => `${e.id}|${e.msg}`).join('||')])

  /** Tela de "Desconto salvo" para o código que o servidor devolveu (ou que achamos depois de uma resposta perdida). */
  const finishSaved = useCallback((code: string, c: Ctx, d: Draft) => {
    const at = new Date()
    const rc = receiptCtx.current ?? c
    const info: SavedInfo = {
      code,
      title: savedTitle(d, c),
      status: savedStatus(d, { ...c, now: at }),
      sentence: sentenceRich(d, c),
      receipts: receipts(d, rc, at),
      note: savedNote(d),
    }
    lastSaved.current = { draft: d, ctx: { ...c, now: at } }
    setSaved(info)
    setView(WizardView.saved)
    focusTop('ok-t')
    queryClient.invalidateQueries({ queryKey: ['discounts'] })
    // "Parecido com…" precisa enxergar o desconto que acabou de entrar
    queryClient.invalidateQueries({ queryKey: ['discountV2', chainId, 'recent'] })
    // Padrão que dividiu outro: descobre o código novo do que sobrou nos outros postos
    if (replacements(d, rc).some(r => r.hasRest)) {
      data.refetchDefaults().then(after => {
        const codes = findSplitCodes(rc.defaults, selIds(d, rc), after, code)
        if (Object.keys(codes).length) setSaved(s => (s ? { ...s, receipts: receipts(d, rc, at, codes) } : s))
      }).catch(() => undefined)
    } else {
      data.refetchDefaults().catch(() => undefined)
    }
    receiptCtx.current = null
    attemptStart.current = null
    unsure.current = false
  }, [queryClient, chainId, data])

  const save = useCallback(async (override?: Ctx) => {
    const c = override ?? ctx
    if (!c || savingRef.current) return
    const bad = firstInvalidStep(draft, c)
    if (bad) {
      setStepIdx(bad.index)
      setAttempted(a => ({ ...a, [bad.step]: true }))
      focusTop('erros')
      return
    }
    savingRef.current = true
    setView(WizardView.saving)
    setFailure(null)
    const payload = toPayload(draft, c)
    receiptCtx.current = receiptCtx.current ?? c
    try {
      if (unsure.current) {
        // A resposta da tentativa anterior se perdeu: vê se o servidor criou o desconto antes de criar outro.
        try {
          const me = getUserId()
          const res: any = await dataProvider.getList('discounts', {
            pagination: { page: 1, perPage: 5 }, sort: { field: 'createdAt', order: 'DESC' },
            filter: { chainId, type: payload.type, ...(me ? { createdByEmployeeId: me } : {}) },
          })
          const found = findSavedDuplicate(payload, Array.isArray(res?.data) ? res.data : [], attemptStart.current ?? Date.now())
          if (found) { finishSaved(String(found.identify ?? ''), c, draft); return }
        } catch {
          setFailure({ ...checkFailInfo(), lines: [], checkList: true })
          setView(WizardView.form)
          setStepIdx(keys.length - 1)
          focusTop('erros-salvar')
          return
        }
      }
      attemptStart.current = attemptStart.current ?? Date.now()
      const res: any = await dataProvider.create('discounts', { data: payload })
      finishSaved(String(res?.data?.identify ?? ''), c, draft)
    } catch (error) {
      unsure.current = isAmbiguousFailure(error)
      if (!unsure.current) attemptStart.current = null
      setFailure(describeFailure(error, payload, c, replacements(draft, c).length > 0))
      setView(WizardView.form)
      setStepIdx(keys.length - 1)
      focusTop('erros-salvar')
    } finally {
      savingRef.current = false
    }
  }, [ctx, draft, dataProvider, chainId, finishSaved, keys.length])

  const retry = useCallback(async () => {
    if (!ctx) return
    // Padrão que trocava outro: confere o estado dos postos antes de tentar de novo (o salvamento pode ter parado no meio)
    if (replacements(draft, ctx).length > 0) {
      try {
        const fresh = await data.refetchDefaults()
        await save({ ...ctx, defaults: fresh })
        return
      } catch {
        /* sem conferência, tenta com o que já tem */
      }
    }
    await save()
  }, [ctx, draft, data, save])

  const next = () => {
    if (!ctx || saving) return
    const errs = errorsFor(stepKey, draft, ctx)
    if (errs.length) {
      setAttempted(a => ({ ...a, [stepKey]: true }))
      focusTop('erros')
      return
    }
    if (last) { void save(); return }
    const n = idx + 1
    setStepIdx(n)
    setMaxStep(m => Math.max(m, n))
  }
  const back = () => {
    if (saving || idx === 0) return
    setStepIdx(idx - 1)
  }
  const cancel = () => (dirty && !canBlock ? setAskLeave(true) : navigate('/discounts'))

  const wiz: Wiz | null = ctx ? {
    d: draft, c: ctx, set, fe, locked: isLocked(draft), step: stepKey, stepIndex: idx, maxStep, goStep, act,
    sameText, setSameText,
    lists: data.lists, cityStats: data.cityStats, audienceCount,
  } : null

  const staff = getEmployeeType() === EmployeeType.admin
  const needsClassic = !!ctx && (ctx.chain.useDotz || ctx.chain.isPriceIncreaseEnabled)
  const unsupported = !!prefill?.unsupported

  const info = stepInfo(stepKey)
  const nextLabel = saving
    ? __t('Salvando…')
    : last
      ? (ctx && draft.kind === DiscountKind.default && replacements(draft, ctx).length ? __t('Salvar e substituir o Padrão') : __t('Salvar desconto'))
      : __t('Próximo: %{etapa}', { etapa: stepInfo(keys[idx + 1]).label })

  let body: React.ReactNode = null
  if (stepKey === StepKey.type) body = <StepType />
  else if (stepKey === StepKey.values) body = <StepValues />
  else if (stepKey === StepKey.who) body = <AudienceSection />
  else if (stepKey === StepKey.when) body = <WhenSection />
  else if (stepKey === StepKey.notice) body = <StepNotice />
  else body = <StepReview />

  const formCard = wiz && ctx ? (
    <Box component="section" aria-labelledby="step-q" sx={{ ...kitCardSx, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ px: narrow ? 2 : 3.5, pt: 2.25 }}>
        <Stepper steps={keys.map(k => ({ key: k, label: stepInfo(k).label }))} current={idx} maxStep={maxStep} busy={saving} onGo={goStep} onCompactChange={setCompactSteps} />
      </Box>
      <Box sx={{ px: narrow ? 2 : 3.5, pt: 2.25, pb: 0.75, mt: 1.75, display: 'flex', flexDirection: 'column', gap: 0.75, borderTop: `1px solid ${C.lineSoft}` }}>
        {compactSteps ? null : <Box component="span" sx={{ fontSize: 13, color: C.quiet }}>{__t('Etapa %{n} de %{total}', { n: idx + 1, total: keys.length })}</Box>}
        <Typography id="step-q" component="h2" tabIndex={-1} sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, color: C.ink, outline: 'none', scrollMarginTop: '72px' }}>{info.question}</Typography>
      </Box>
      <WizardContext.Provider value={wiz}>
        <Box sx={{ px: narrow ? 2 : 3.5, pt: 2.25, pb: 3.5, display: 'flex', flexDirection: 'column', gap: 3.5, maxWidth: 968, minWidth: 0 }}>
          {copyNote ? (
            <Callout tone="info" icon={<InfoOutlinedIcon />}>{copyNote}</Callout>
          ) : null}
          {errors.length ? <ErrorSummary errors={errors} /> : null}
          {failure && last ? (
            <Callout tone="error" role="alert" id="erros-salvar" tabIndex={-1}>
              <Box sx={stackStartSx}>
                <span><b>{failure.title}</b> {failure.text}</span>
                {failure.lines.length ? (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{failure.lines.map((l, i) => <li key={i}>{l}</li>)}</Box>
                ) : null}
                <Box sx={buttonsRowSx}>
                  <OutlineButton onClick={() => { void retry() }} sx={retryButtonSx}>{failure.button}</OutlineButton>
                  {failure.checkList ? (
                    <OutlineButton component="a" extra={LIST_LINK} sx={retryButtonSx}>{__t('Abrir a lista de descontos em outra aba')}</OutlineButton>
                  ) : null}
                </Box>
              </Box>
            </Callout>
          ) : null}
          {body}
        </Box>
      </WizardContext.Provider>
      <Footer narrow={narrow} canBack={idx > 0 && !saving} onBack={back} onCancel={cancel} onNext={next} nextLabel={nextLabel} last={last} saving={saving} />
    </Box>
  ) : null

  return (
    <Box ref={boxRef} sx={{ width: '100%', maxWidth: 1240, mx: 'auto', px: narrow ? 1 : 3, pt: 2, pb: 10, minWidth: 0 }}>
      {staff && ctx ? (
        <Callout tone="info" sx={{ mb: 2 }}>{__t('Você está como equipe PontuaX na rede %{rede}. O desconto vai ser criado nesta rede.', { rede: ctx.chain.name || getChainName() || '' })}</Callout>
      ) : null}

      <Box id="topo" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, scrollMarginTop: '72px' }}>
        <Box component="nav" aria-label={__t('Caminho')} sx={{ display: 'flex', gap: 0.75, alignItems: 'center', fontSize: 13, color: C.quiet }}>
          <Link to="/discounts" style={{ color: C.violet, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', minHeight: 40 }}>{__t('Descontos')}</Link>
          <span aria-hidden>/</span>
          <span>{__t('Novo desconto')}</span>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, color: C.ink }}>{__t('Novo desconto')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {needsClassic ? (
              <TextButton onClick={() => navigate('/discounts/create-classic')} sx={{ fontSize: 13 }}>{__t('Usar o cadastro completo (Dotz e acréscimo)')}</TextButton>
            ) : null}
            <FeatureIntro id="manual-como-criar-desconto-cadastro" title={__t('Como funciona')} description={__t('Um guia curto com cada tipo de desconto, com exemplos e o que o cliente vê no app.')}>
              <Box sx={manualButtonSx}>
                <ManualLinkButton slug="como-criar-desconto" label={__t('Como funciona')} />
              </Box>
            </FeatureIntro>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {data.failed ? (
          <Callout tone="error" role="alert">
            <Box sx={stackStartSx}>
              <span>{__t('Não foi possível carregar os dados da rede.')}</span>
              <OutlineButton onClick={data.retry} sx={{ height: 40, px: 1.75, fontSize: 14 }}>{__t('Tentar de novo')}</OutlineButton>
            </Box>
          </Callout>
        ) : !ctx || !wiz ? (
          <Box sx={fillSx}><Loading /></Box>
        ) : unsupported ? (
          <Box sx={fillSx}>
            <Callout tone="warn" role="alert" id="nao-suportado">
              <Box sx={stackStartSx}>
                <span>{__t('Este desconto usa Dotz, porcentagem, valor fixo, acréscimo de preço ou mais de 3 opções num mesmo combustível. Esses formatos só existem no cadastro completo.')}</span>
                <OutlineButton onClick={() => navigate('/discounts/create-classic', { state: location.state })} sx={{ height: 40, px: 1.75, fontSize: 14 }}>{__t('Abrir no cadastro completo')}</OutlineButton>
              </Box>
            </Callout>
          </Box>
        ) : (
          <Box sx={fillSx}>
            {showingSaved && saved ? (
              <SavedScreen
                narrow={narrow}
                info={saved}
                onNew={() => resetFlow(freshDraft(), 0)}
                onDuplicate={() => {
                  const snap = lastSaved.current
                  if (!snap) return
                  const copy = duplicateDraft(snap.draft, snap.ctx)
                  resetFlow(copy.draft, 1, copy.note)
                }}
              />
            ) : formCard}
          </Box>
        )}
      </Box>

      {canBlock
        ? <LeaveGuard dirty={dirty} />
        : <LeaveDialog open={askLeave} onStay={() => setAskLeave(false)} onLeave={() => navigate('/discounts')} />}
    </Box>
  )
}

/** Cadastro de desconto (v2): um passo por pergunta, com a frase do desconto e a prévia do app sempre à vista. */
const DiscountCreateV2: React.FC = () => {
  const chainId = getChainId()
  return (
    <>
      <Title title="Novo desconto" />
      {chainId ? <Wizard key={chainId} chainId={chainId} /> : <NoChain />}
    </>
  )
}

export default DiscountCreateV2
